import { ref } from 'vue';
import { permissionApi } from '../api/permission.api';
import type {
  PermissionCategory,
  PermissionMatrixPositionType,
} from './types';
import { sortCategories } from './sortCategories';
import { createCategoryDependencies } from './categoryPermissionDependencies';
import { useAdminRowPermissions } from './useAdminRowPermissions';
import { useTrainingRowPermissions } from './useTrainingRowPermissions';
import { useDirectoryRowPermissions } from './useDirectoryRowPermissions';

interface PendingChange {
  positionId: number;
  categoryId: number;
  permissionIds: number[];
}

interface PermissionLike {
  id: number;
  name: string;
  description: string;
}

const getPermissionsPayload = (response: unknown): unknown => {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  return (response as { data?: unknown }).data;
};

const extractCategories = (response: unknown): PermissionCategory[] => {
  if (Array.isArray(response)) {
    return response;
  }

  const payload = getPermissionsPayload(response);
  if (Array.isArray(payload)) {
    return payload;
  }

  const nestedPayload = getPermissionsPayload(payload);
  if (Array.isArray(nestedPayload)) {
    return nestedPayload;
  }

  return [];
};

const buildPermissionLookup = (permissions: PermissionLike[]): Map<number, PermissionLike> => {
  return new Map<number, PermissionLike>(permissions.map((permission) => [permission.id, permission]));
};

/**
 * Marcar "Verificação" marca automaticamente "Aprovação"; desmarcar "Aprovação"
 * remove "Verificação".
 */
const applyHomologationDependencies = createCategoryDependencies({
  categoryName: 'Homologação',
  basePermissionKeys: ['manageApproval', 'manageApproval:Homologation'],
  basePermissionDescriptions: ['Aprovação'],
  dependentPermissionKeys: ['manageVerification', 'manageVerification:Homologation'],
  dependentPermissionDescriptions: ['Verificação'],
});

const resolvePermissionsForChange = (
  change: PendingChange,
  categoryPermissionLookup: Map<number, PermissionLike>,
  existingPermissionLookup: Map<number, PermissionLike>
): PermissionLike[] => {
  return change.permissionIds
    .map(
      (permissionId) =>
        categoryPermissionLookup.get(permissionId) ?? existingPermissionLookup.get(permissionId)
    )
    .filter((permission): permission is PermissionLike => Boolean(permission));
};

const updatePositionPermissions = (
  position: PermissionCategory['positions'][number],
  change: PendingChange,
  categoryPermissionLookup: Map<number, PermissionLike>
) => {
  if (position.id !== change.positionId) {
    return position;
  }

  const existingPermissionLookup = buildPermissionLookup(position.permissions);

  return {
    ...position,
    permissions: resolvePermissionsForChange(
      change,
      categoryPermissionLookup,
      existingPermissionLookup
    ),
  };
};

const applyChangeToCategory = (category: PermissionCategory, change: PendingChange) => {
  if (category.id !== change.categoryId) {
    return category;
  }

  const categoryPermissionLookup = buildPermissionLookup(category.permissions);

  return {
    ...category,
    positions: category.positions.map((position) =>
      updatePositionPermissions(position, change, categoryPermissionLookup)
    ),
  };
};

export function usePermissionsMatrix() {
  const { applyAdminDependencies } = useAdminRowPermissions();
  const { applyTrainingDependencies } = useTrainingRowPermissions();
  const { applyDirectoryDependencies } = useDirectoryRowPermissions();

  const categories = ref<PermissionCategory[]>([]);
  const originalCategoriesOrder = ref<Map<number, PermissionCategory>>(new Map());
  const pendingChanges = ref<Map<string, PendingChange>>(new Map());
  const isLoading = ref(false);
  const isSaving = ref(false);
  const error = ref<string | null>(null);
  const currentPositionType = ref<PermissionMatrixPositionType>('POSITION');

  const getPendingChangeKey = (positionId: number, categoryId: number): string => {
    return `${positionId}-${categoryId}`;
  };

  const applySavedPermissionsToCategory = (change: PendingChange) => {
    categories.value = categories.value.map((category) => applyChangeToCategory(category, change));
  };

  const fetchPermissionsMatrix = async (
    positionType: PermissionMatrixPositionType = currentPositionType.value
  ) => {
    isLoading.value = true;
    error.value = null;
    currentPositionType.value = positionType;

    try {
      const response = await permissionApi.getPermissionsMatrix(positionType);
      categories.value = extractCategories(response);
      pendingChanges.value.clear();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro desconhecido';
      console.error('Error fetching permissions matrix:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const getPositionPermissionIds = (positionId: number, categoryId: number): number[] => {
    const key = getPendingChangeKey(positionId, categoryId);
    const pending = pendingChanges.value.get(key);

    if (pending) {
      return pending.permissionIds;
    }

    const category = categories.value.find((cat) => cat.id === categoryId);
    if (!category) return [];

    const position = category.positions.find((pos) => pos.id === positionId);
    if (!position) return [];

    return position.permissions.map((perm) => perm.id);
  };

  const hasPermission = (positionId: number, categoryId: number, permissionId: number): boolean => {
    const permissionIds = getPositionPermissionIds(positionId, categoryId);
    return permissionIds.includes(permissionId);
  };

  const togglePermission = (positionId: number, categoryId: number, permissionId: number) => {
    const currentPermissions = getPositionPermissionIds(positionId, categoryId);
    const hasPermissionValue = currentPermissions.includes(permissionId);
    const category = categories.value.find((item) => item.id === categoryId);

    const basePermissions = hasPermissionValue
      ? currentPermissions.filter((id) => id !== permissionId)
      : [...currentPermissions, permissionId];
    const newPermissions = category
      ? applyHomologationDependencies(category, currentPermissions, permissionId) ??
        applyAdminDependencies(category, currentPermissions, permissionId) ??
        applyTrainingDependencies(category, currentPermissions, permissionId) ??
        applyDirectoryDependencies(category, currentPermissions, permissionId) ??
        basePermissions
      : basePermissions;

    const key = getPendingChangeKey(positionId, categoryId);
    pendingChanges.value.set(key, {
      positionId,
      categoryId,
      permissionIds: newPermissions,
    });
  };

  const hasPendingChanges = (categoryId: number): boolean => {
    return Array.from(pendingChanges.value.values()).some(
      (change) => change.categoryId === categoryId
    );
  };

  const saveCategoryChanges = async (categoryId: number) => {
    isSaving.value = true;
    error.value = null;

    try {
      const changesToSave = Array.from(pendingChanges.value.values()).filter(
        (change) => change.categoryId === categoryId
      );

      for (const change of changesToSave) {
        await permissionApi.updatePositionPermissions(change, currentPositionType.value);
        applySavedPermissionsToCategory(change);
        pendingChanges.value.delete(getPendingChangeKey(change.positionId, change.categoryId));
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao salvar permissões';
      throw err;
    } finally {
      isSaving.value = false;
    }
  };

  const discardCategoryChanges = (categoryId: number) => {
    const keysToDelete = Array.from(pendingChanges.value.entries())
      .filter(([, change]) => change.categoryId === categoryId)
      .map(([key]) => key);

    keysToDelete.forEach((key) => pendingChanges.value.delete(key));
  };

  const sortCategory = (categoryId: number, sort: string, order: string) => {
    categories.value = sortCategories(
      categories.value,
      categoryId,
      sort,
      order,
      originalCategoriesOrder.value
    );
  };

  return {
    categories,
    isLoading,
    isSaving,
    error,
    fetchPermissionsMatrix,
    getPositionPermissionIds,
    hasPermission,
    togglePermission,
    hasPendingChanges,
    saveCategoryChanges,
    discardCategoryChanges,
    sortCategory,
  };
}
